import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SustainabilityMetrics {
  overview: {
    totalProducts: number;
    saferIngredientsPercentage: number;
    carbonReduced: number;
    waterSaved: number;
    wasteReduced: number;
  };
  trends: {
    monthlyData: Array<{
      month: string;
      products: number;
      saferIngredients: number;
      carbon: number;
      water: number;
    }>;
  };
  categoryBreakdown: {
    riskLevels: Array<{
      level: string;
      count: number;
      percentage: number;
    }>;
    productTypes: Array<{
      type: string;
      count: number;
      saferPercentage: number;
    }>;
  };
  certificationStatus: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  topPerformers: Array<{
    productName: string;
    saferPercentage: number;
    riskScore: number;
  }>;
  loading: boolean;
  error: string | null;
}

export const useEnhancedSustainabilityMetrics = () => {
  const [metrics, setMetrics] = useState<SustainabilityMetrics>({
    overview: {
      totalProducts: 0,
      saferIngredientsPercentage: 0,
      carbonReduced: 0,
      waterSaved: 0,
      wasteReduced: 0,
    },
    trends: {
      monthlyData: [],
    },
    categoryBreakdown: {
      riskLevels: [],
      productTypes: [],
    },
    certificationStatus: [],
    topPerformers: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    const fetchEnhancedMetrics = async () => {
      try {
        setMetrics(prev => ({ ...prev, loading: true, error: null }));

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('User not authenticated');
        }

        // Fetch comprehensive product data
        const { data: portfolios, error: portfoliosError } = await supabase
          .from('portfolios')
          .select(`
            id,
            title,
            created_at,
            products (
              id,
              name,
              product_type,
              certification_status,
              created_at,
              product_chemicals (
                id,
                percentage_typical,
                chemical:chemicals (
                  id,
                  chemical_name,
                  chemical_endpoints (
                    risk_level,
                    endpoint:endpoint_definitions (
                      category
                    )
                  )
                )
              )
            )
          `)
          .eq('user_id', user.id);

        if (portfoliosError) throw portfoliosError;

        // Calculate comprehensive metrics
        let totalProducts = 0;
        let totalChemicals = 0;
        let saferChemicals = 0;
        let riskLevelCounts = { low: 0, moderate: 0, high: 0, unknown: 0 };
        let productTypeCounts: { [key: string]: { total: number; safer: number } } = {};
        let certificationCounts: { [key: string]: number } = {};
        let monthlyProductData: { [key: string]: number } = {};
        let productPerformances: Array<{ productName: string; saferPercentage: number; riskScore: number }> = [];

        portfolios?.forEach(portfolio => {
          if (portfolio.products) {
            portfolio.products.forEach(product => {
              totalProducts++;
              
              // Track monthly trends
              const monthKey = new Date(product.created_at).toISOString().slice(0, 7);
              monthlyProductData[monthKey] = (monthlyProductData[monthKey] || 0) + 1;
              
              // Track certification status
              certificationCounts[product.certification_status] = 
                (certificationCounts[product.certification_status] || 0) + 1;
              
              // Initialize product type tracking
              if (!productTypeCounts[product.product_type]) {
                productTypeCounts[product.product_type] = { total: 0, safer: 0 };
              }
              productTypeCounts[product.product_type].total++;
              
              // Analyze chemical composition
              let productChemicals = 0;
              let productSaferChemicals = 0;
              let productRiskScores: number[] = [];
              
              if (product.product_chemicals) {
                product.product_chemicals.forEach(productChemical => {
                  if (productChemical.chemical?.chemical_endpoints) {
                    productChemicals++;
                    totalChemicals++;
                    
                    const endpoints = productChemical.chemical.chemical_endpoints;
                    const riskLevels = endpoints.map(ep => ep.risk_level);
                    
                    // Calculate risk scores for trending
                    const riskScore = riskLevels.reduce((acc, level) => {
                      switch (level) {
                        case 'low': return acc + 0.1;
                        case 'moderate': return acc + 0.5;
                        case 'high': return acc + 1.0;
                        default: return acc + 0.3;
                      }
                    }, 0) / riskLevels.length;
                    
                    productRiskScores.push(riskScore);
                    
                    // Count risk levels
                    riskLevels.forEach(level => {
                      riskLevelCounts[level as keyof typeof riskLevelCounts]++;
                    });
                    
                    // Determine if chemical is "safer"
                    const lowRiskCount = riskLevels.filter(level => level === 'low').length;
                    const moderateRiskCount = riskLevels.filter(level => level === 'moderate').length;
                    const totalEndpoints = riskLevels.length;
                    
                    if (totalEndpoints > 0) {
                      const saferEndpoints = lowRiskCount + moderateRiskCount;
                      if (saferEndpoints / totalEndpoints >= 0.6) {
                        productSaferChemicals++;
                        saferChemicals++;
                      }
                    }
                  }
                });
              }
              
              // Track product performance
              const productSaferPercentage = productChemicals > 0 
                ? (productSaferChemicals / productChemicals) * 100 
                : 0;
              const avgRiskScore = productRiskScores.length > 0 
                ? productRiskScores.reduce((a, b) => a + b, 0) / productRiskScores.length 
                : 0;
                
              productPerformances.push({
                productName: product.name,
                saferPercentage: productSaferPercentage,
                riskScore: avgRiskScore,
              });
              
              // Update product type safer count
              if (productSaferPercentage >= 60) {
                productTypeCounts[product.product_type].safer++;
              }
            });
          }
        });

        // Calculate overview metrics
        const saferIngredientsPercentage = totalChemicals > 0 
          ? Math.round((saferChemicals / totalChemicals) * 100) 
          : 0;
        
        // Enhanced sustainability calculations
        const carbonReduced = totalProducts * 12.5 + (saferChemicals * 2.3); // Enhanced formula
        const waterSaved = totalProducts * 150 + (saferChemicals * 25); // Enhanced formula
        const wasteReduced = totalProducts * 8.7 + (saferIngredientsPercentage * 1.2); // New metric

        // Generate monthly trends (last 6 months)
        const monthlyData = Object.entries(monthlyProductData)
          .sort(([a], [b]) => a.localeCompare(b))
          .slice(-6)
          .map(([month, products]) => ({
            month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
            products,
            saferIngredients: Math.round(products * (saferIngredientsPercentage / 100)),
            carbon: products * 12.5,
            water: products * 150,
          }));

        // Risk level breakdown
        const totalRiskAssessments = Object.values(riskLevelCounts).reduce((a, b) => a + b, 0);
        const riskLevels = Object.entries(riskLevelCounts).map(([level, count]) => ({
          level: level.charAt(0).toUpperCase() + level.slice(1),
          count,
          percentage: totalRiskAssessments > 0 ? Math.round((count / totalRiskAssessments) * 100) : 0,
        }));

        // Product type breakdown
        const productTypes = Object.entries(productTypeCounts).map(([type, data]) => ({
          type: type.charAt(0).toUpperCase() + type.slice(1),
          count: data.total,
          saferPercentage: data.total > 0 ? Math.round((data.safer / data.total) * 100) : 0,
        }));

        // Certification status breakdown
        const totalCertifications = Object.values(certificationCounts).reduce((a, b) => a + b, 0);
        const certificationStatus = Object.entries(certificationCounts).map(([status, count]) => ({
          status: status.charAt(0).toUpperCase() + status.slice(1),
          count,
          percentage: totalCertifications > 0 ? Math.round((count / totalCertifications) * 100) : 0,
        }));

        // Top performers
        const topPerformers = productPerformances
          .sort((a, b) => b.saferPercentage - a.saferPercentage)
          .slice(0, 5);

        setMetrics({
          overview: {
            totalProducts,
            saferIngredientsPercentage,
            carbonReduced: Math.round(carbonReduced),
            waterSaved: Math.round(waterSaved),
            wasteReduced: Math.round(wasteReduced),
          },
          trends: {
            monthlyData,
          },
          categoryBreakdown: {
            riskLevels,
            productTypes,
          },
          certificationStatus,
          topPerformers,
          loading: false,
          error: null,
        });

      } catch (error) {
        console.error('Error fetching enhanced sustainability metrics:', error);
        setMetrics(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to fetch enhanced metrics',
        }));
      }
    };

    fetchEnhancedMetrics();
  }, []);

  return metrics;
};